/**
 * Sentral i18n-infrastruktur (issue #13280).
 *
 * - Bygger på i18next + react-i18next slik at både Lit-komponentene (~90 % av UI)
 *   og React-chatten deler én instans.
 * - Standardspråk er norsk (nb). Engelsk, svensk, dansk og polsk er støttet;
 *   manglende nøkler faller tilbake til norsk.
 * - Brukervalg lagres i localStorage ('dabih-language') og gjelder per enhet.
 *   Det finnes ingen per-bruker-endepunkt i API-klienten ennå; `persistLanguage`
 *   er det eneste skrivepunktet og kan utvides til server-synk senere.
 * - Språkbytte gjør `location.reload()` slik at alle komponenter rendres på nytt
 *   med riktig språk ved første paint (aktivt språk leses synkront ved oppstart).
 *
 * Denne modulen MÅ importeres før app-root i src/index.ts.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { SupportedLanguage } from './types';

import nbCommon from './locales/nb/common.json';
import nbUi from './locales/nb/ui.json';
import enCommon from './locales/en/common.json';
import enUi from './locales/en/ui.json';
import svCommon from './locales/sv/common.json';
import svUi from './locales/sv/ui.json';
import daCommon from './locales/da/common.json';
import daUi from './locales/da/ui.json';
import plCommon from './locales/pl/common.json';
import plUi from './locales/pl/ui.json';

export const LANGUAGE_STORAGE_KEY = 'dabih-language';
export const FALLBACK_LANGUAGE = 'nb';

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['nb', 'en', 'sv', 'da', 'pl'];

/** Visningsnavn for språkvelgeren — alltid på språket selv. */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  nb: 'Norsk',
  en: 'English',
  sv: 'Svenska',
  da: 'Dansk',
  pl: 'Polski',
};

/** Korte koder brukt i den kompakte språkvelgeren (NO, EN, …). */
export const LANGUAGE_CODES: Record<SupportedLanguage, string> = {
  nb: 'NO',
  en: 'EN',
  sv: 'SV',
  da: 'DA',
  pl: 'PL',
};

/** BCP-47-tagger brukt av Intl for lokaliserte dato-/tidsformater. */
export const LOCALE_TAGS: Record<SupportedLanguage, string> = {
  nb: 'nb-NO',
  en: 'en-GB',
  sv: 'sv-SE',
  da: 'da-DK',
  pl: 'pl-PL',
};

function isSupported(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/** Normaliserer en rå språkkode ('en-US', 'nb_NO', 'no') til et støttet språk. */
function normalize(raw: string | null | undefined): SupportedLanguage | undefined {
  if (!raw) {
    return undefined;
  }
  const base = raw.toLowerCase().split(/[-_]/)[0];
  if (base === 'no' || base === 'nn' || base === 'nb') {
    return 'nb';
  }
  return isSupported(base) ? base : undefined;
}

function readStoredLanguage(): string | undefined {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? undefined;
  } catch {
    // localStorage utilgjengelig (privat modus o.l.) — fortsett uten lagret valg.
    return undefined;
  }
}

/** Per-org cache av hseProduct-status, slik at init blir synkron uten reload-loop. */
const HSE_ORG_CACHE_KEY = 'dabih-hse-orgs';

/** Org-id fra URL-en (/account/{id}/...). Undefined på innlogging / uten org. */
function currentOrganizationIdFromUrl(): number | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const match = window.location.pathname.match(/\/account\/(\d+)/);
  return match ? Number(match[1]) : undefined;
}

function readHseOrgCache(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(HSE_ORG_CACHE_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

/** Brukerens lagrede språkvalg (gjelder KUN i org-er med hseProduct). */
function preferredLanguage(): SupportedLanguage {
  return (
    normalize(readStoredLanguage()) ??
    normalize(typeof navigator !== 'undefined' ? navigator.language : undefined) ??
    FALLBACK_LANGUAGE
  );
}

/**
 * Velger aktivt språk ved oppstart. Språkvalg gjelder KUN for kunder med
 * hseProduct: i en org uten hseProduct — eller før produktet er kjent, eller på
 * innloggingssiden — er norsk gjeldende. Org-ens hseProduct-status leses fra en
 * per-org cache som `applyOrganizationLanguage` holder oppdatert, slik at init
 * er synkron (ingen FOUC) og ikke gir reload-loop.
 */
export function detectInitialLanguage(): SupportedLanguage {
  const organizationId = currentOrganizationIdFromUrl();
  if (organizationId === undefined) {
    return FALLBACK_LANGUAGE; // innlogging / uten org → norsk
  }
  const hasHseProduct = readHseOrgCache()[String(organizationId)] === true;
  return hasHseProduct ? preferredLanguage() : FALLBACK_LANGUAGE;
}

const activeLanguage: SupportedLanguage = detectInitialLanguage();

// Synkron init: ressursene er bundlet (ingen async backend), så `t()` virker
// umiddelbart etter dette kallet.
void i18next.use(initReactI18next).init({
  lng: activeLanguage,
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  ns: ['common', 'ui'],
  defaultNS: 'ui',
  resources: {
    nb: { common: nbCommon, ui: nbUi },
    en: { common: enCommon, ui: enUi },
    sv: { common: svCommon, ui: svUi },
    da: { common: daCommon, ui: daUi },
    pl: { common: plCommon, ui: plUi },
  },
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = activeLanguage;
}

export const i18n = i18next;

export function currentLanguage(): SupportedLanguage {
  return activeLanguage;
}

export function currentLocaleTag(): string {
  return LOCALE_TAGS[activeLanguage];
}

// Løs typing for i18next.t for å unngå de tunge generiske typene på tvers av
// hundrevis av kallsteder; nøklene valideres i praksis av tester og fallback.
type TranslateFn = (key: string, options?: Record<string, unknown>) => unknown;

/** Oversetter en nøkkel til en streng (standard bruk i maler). */
export function t(key: string, options?: Record<string, unknown>): string {
  return (i18next.t as TranslateFn)(key, options) as string;
}

/** Henter en oversatt liste (f.eks. ukedagsnavn) som string[]. */
export function tArray(key: string): string[] {
  const value = (i18next.t as TranslateFn)(key, { returnObjects: true });
  return Array.isArray(value) ? (value as string[]) : [];
}

/**
 * Skrivepunkt for språkvalget. Lagrer i localStorage og laster siden på nytt.
 * Eneste sted som persisterer språk — utvid her for server-synk når et
 * per-bruker-endepunkt finnes.
 */
export function setLanguage(language: SupportedLanguage): void {
  if (language === activeLanguage) {
    return;
  }
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // localStorage utilgjengelig — kan ikke persistere valget; ingenting mer å gjøre.
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
  // Reload slik at hele appen (Lit + React) rendres på det nye språket.
  location.reload();
}

/**
 * Avstemmer aktivt språk mot org-ens hseProduct-status. Kalles av appen når den
 * gjeldende org-ens featureStates er kjent. Uten hseProduct er norsk gjeldende;
 * med hseProduct gjelder brukerens språkvalg. Cacher hseProduct per org (slik at
 * neste init blir riktig uten ny reload) og laster siden på nytt — samme modell
 * som `setLanguage` — kun hvis gjeldende språk faktisk avviker. Dette gir at en
 * flerorg-bruker får riktig språk per org: norsk i org-er uten hseProduct.
 */
export function applyOrganizationLanguage(organizationId: number, hasHseProduct: boolean): void {
  if (organizationId > 0) {
    try {
      const cache = readHseOrgCache();
      if (cache[String(organizationId)] !== hasHseProduct) {
        cache[String(organizationId)] = hasHseProduct;
        localStorage.setItem(HSE_ORG_CACHE_KEY, JSON.stringify(cache));
      }
    } catch {
      // localStorage utilgjengelig — fortsett uten cache (kan gi én ekstra reload).
    }
  }
  const effective = hasHseProduct ? preferredLanguage() : FALLBACK_LANGUAGE;
  if (effective !== activeLanguage) {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = effective;
    }
    location.reload();
  }
}
